/**
 * Links into the Tasks tab, written by the API into brief and summary markdown
 * and read back by the apps.
 *
 * `/tasks/:id?answer=done` names an answer, but following it never gives one.
 * Mail scanners and link previews fetch every link in an email, so an answer
 * that a page load applied would close tasks nobody touched. The web thread
 * renders the link as a button that answers on a tap; everywhere else it opens
 * the task in the Tasks tab, where the user answers there.
 */

export const USER_TASK_ANSWERS = [
  "done",
  "later",
  "drop",
  "keep",
  "dismiss",
  "reopen",
  "gotIt",
  "stillWaiting",
  "handOff",
] as const;

export type UserTaskAnswer = (typeof USER_TASK_ANSWERS)[number];

/**
 * Each answer's link text. The web thread shows a button only for a link that
 * reads as its own answer — a task title linked to `?answer=done` by mistake
 * must not close the task when tapped.
 */
export const USER_TASK_ANSWER_LABELS: Record<UserTaskAnswer, string> = {
  done: "Done",
  later: "Later",
  drop: "Drop",
  keep: "Keep",
  dismiss: "Dismiss",
  reopen: "Reopen",
  gotIt: "Got it",
  stillWaiting: "Still waiting",
  handOff: "Hand to Okthx",
};

const TASKS_PATH = "/tasks";

/** The Tasks tab, or one task in it. */
export function userTaskHref(taskId?: string): string {
  return taskId ? `${TASKS_PATH}/${taskId}` : TASKS_PATH;
}

/**
 * A label's page in the Tasks tab. A segment of its own under `/tasks` rather
 * than `/tasks/labels/:id`: the web app reads the last path segment as the item
 * the Tasks tab opens, and a task's id is all digits, so `label-` cannot be one.
 */
export function userTaskLabelHref(labelId: string): string {
  return `${TASKS_PATH}/label-${labelId}`;
}

/** The label a `/tasks/label-:id` item names, or null for a task. */
export function parseUserTaskLabelItem(itemId: string): string | null {
  return /^label-(\d+)$/.exec(itemId)?.[1] ?? null;
}

/**
 * The page behind the Tasks toolbar's sliders: what okthx reads to make
 * tasks, the senders it must not read, and the user's own rules about what
 * counts as one.
 *
 * A segment of its own under `/tasks`, for the reason `label-` is one: the
 * apps read the last segment as the item the tab opens, and a task's id is
 * all digits, so the words cannot be mistaken for one.
 */
const USER_TASK_SOURCES_ITEM = "sources-and-rules";

/** The path of the sources-and-rules page in the Tasks tab. */
export function userTaskSourcesHref(): string {
  return `${TASKS_PATH}/${USER_TASK_SOURCES_ITEM}`;
}

/** Whether a `/tasks/:item` names the sources-and-rules page. */
export function isUserTaskSourcesItem(itemId: string): boolean {
  return itemId === USER_TASK_SOURCES_ITEM;
}

/** The path that answers a task's question from a link: `/tasks/:id?answer=…`. */
export function userTaskAnswerHref(taskId: string, answer: UserTaskAnswer): string {
  return `${userTaskHref(taskId)}?answer=${answer}`;
}

function isUserTaskAnswer(value: string): value is UserTaskAnswer {
  return USER_TASK_ANSWERS.some((answer) => answer === value);
}

/** The task and answer a `/tasks/:id?answer=…` path names, or null. */
export function parseUserTaskAnswerHref(
  path: string,
): { taskId: string; answer: UserTaskAnswer } | null {
  const match = /^\/tasks\/(\d+)\?answer=([A-Za-z]+)$/.exec(path);
  if (!match) return null;
  const [, taskId = "", answer = ""] = match;
  return isUserTaskAnswer(answer) ? { taskId, answer } : null;
}
